import { bulletedList, type BulletList } from '@ms-cloudpack/task-reporter';
import semver from 'semver';
import type { ResolvedVersions, ResolvedVersionSpec } from './types/ResolvedVersions.js';

/**
 * Check resolved versions from the lock file for potential issues.
 * @returns A message describing any issues found.
 */
export function checkResolvedVersions(resolvedVersions: ResolvedVersions): string {
  // What the lock file tells you:
  // - non-semver specs used
  // - if two specs resolved to the same package (name, version, and URL)
  //   - if non-semver specs overlap but one is renamed, it's ok
  // What it doesn't tell you:
  // - installation layout
  //   - if multiple versions are present, don't know which is hoisted (or any nohoist);
  //     a non-semver version not being hoisted might resolve an overlap
  // - where the weird dep came from (without walking the dependency graph)

  const messages: BulletList = [];

  for (const { name, version, specs } of Object.values(resolvedVersions)) {
    const issues: BulletList = [];

    const semverSpecs = specs.filter((spec) => !!semver.validRange(spec.specVersion));
    const forcedSpecs = semverSpecs.filter((spec) => !semver.satisfies(version, spec.specVersion));

    if (forcedSpecs.length) {
      // Cloudpack attempts to handle this, but there may be some places which rely on semver checks
      issues.push('Resolutions violating semver:', getStrings({ specs: forcedSpecs, name }));
    }

    if (semverSpecs.length !== specs.length) {
      // Whether or not this will be an issue depends on a lot of details.
      // There are enough permutations of possible issues that it's hard to have distinct logs
      // for all cases, so just go with a general list that includes relevant info.
      issues.push(semverSpecs.length ? 'Mix of semver and non-semver specs:' : 'Non-semver specs:');
      issues.push(
        getStrings({
          specs,
          name,
          // Only include the URL if there are specs with different URLs (reduces noise)
          includeUrl: specs.some((spec) => spec.url !== specs[0].url),
        }),
      );
    }

    if (issues.length) {
      messages.push([`${name} (${version})`, issues]);
    }
  }

  if (messages.length) {
    return (
      'Found possible issues with dependencies based on lock file ' +
      '(use "yarn why" to figure out where these came from):\n\n' +
      bulletedList(messages) +
      '\n'
    );
  }
  return 'No issues found';
}

function getStrings(params: { specs: ResolvedVersionSpec[]; name: string; includeUrl?: boolean }): string[] {
  const { specs, name, includeUrl } = params;
  return specs.map(
    ({ specVersion, realName = name, url }) =>
      specVersion +
      (realName !== name ? ` (real name: ${realName})` : '') +
      (includeUrl && url ? ` resolved to ${url}` : ''),
  );
}
